import React, { useState, useEffect, useContext, useRef } from 'react'
import ROSLIB from 'roslib'
import Context from '../utils/Context'
import ROS from '../utils/ROS/ROS'
import {
  subscribeToRobotPosition,
  subscribeToOccupancyMap,
  subscribeToLaserData as subscribeToLaserTopic,
  measureConnectionLatency,
  unsubscribeFromTopic,
  unsubscribeFromTopics,
  imuCalibration
} from '../utils/ROS/ROSFunctions'
import '../assets/css/rviz.css'

const Rviz = () => {
  const context = useContext(Context)


  const [robotPosition, setRobotPosition] = useState(null)
  const [laserData, setLaserData] = useState({
    left: null,
    right: null,
    top: null,
    front: null,
    rear: null,
    generic: null
  })
  const [isConnected, setIsConnected] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [obstacleAlerts, setObstacleAlerts] = useState({
    left: false,
    right: false,
    top: false,
    front: false,
    rear: false,
    generic: false
  })
  const [minDistances, setMinDistances] = useState({
    left: null,
    right: null,
    top: null,
    front: null,
    rear: null,
    generic: null
  })
  const [isPaused, setIsPaused] = useState(false)
  const [fps, setFps] = useState(0)
  const [latency, setLatency] = useState(null)
  const [lastLatencyCheck, setLastLatencyCheck] = useState(null)
  const [dataStats, setDataStats] = useState({
    totalUpdates: 0,
    startTime: Date.now()
  })
  const [mapData, setMapData] = useState(null)
  const [mapScale, setMapScale] = useState(1)
  const [mapOffset, setMapOffset] = useState({ x: 0, y: 0 })
  const [backgroundMapImage, setBackgroundMapImage] = useState(null)
  const [backgroundMapLoaded, setBackgroundMapLoaded] = useState(false)
  const [showBackgroundMap, setShowBackgroundMap] = useState(true)
  const [robotFootprint, setRobotFootprint] = useState(null)

  // Control state for which lasers are displayed on the map
  const [laserVisibility, setLaserVisibility] = useState({
    front: true,
    rear: true,
    left: true,
    right: true,
    top: true,
    generic: true
  })

  // Fixed laser transformation settings based on TF data from /tf_static
  // Updated to match the generic laser representation (which is the combination of individual lasers)
  // To get exact positions: rostopic echo /tf_static and look for laser transforms
  // CORRECTED: Using TF transformations to match generic laser display
  const laserTransforms = {
    // Front laser (from TF data)
    front: { x: 0.205, y: 0.01, yaw: 0 },
    // Rear laser (estimated based on robot geometry)
    rear: { x: -0.35, y: 0.0, yaw: Math.PI },
    // Left laser - needs 90° rotation to match generic representation
    left: { x: 0.0, y: 0.3, yaw: Math.PI / 2 },
    // Right laser - needs -90° rotation to match generic representation  
    right: { x: 0.0, y: -0.3, yaw: -Math.PI / 2 },
    // Top laser - may need specific rotation based on TF data
    top: { x: 0.0, y: 0.0, yaw: 0 },
    // Generic laser - reference (combination of all others)
    generic: { x: 0, y: 0, yaw: 0 }
  }

  // Refs for subscribers
  const positionSubscriber = useRef(null)
  const laserSubscribers = useRef({
    left: null,
    right: null,
    top: null,
    front: null,
    rear: null,
    generic: null
  })
  const canvasRefs = useRef({
    left: null,
    right: null,
    top: null,
    front: null,
    rear: null,
    generic: null
  })
  const mapCanvasRef = useRef(null)
  const mapSubscriber = useRef(null)
  const footprintSubscriber = useRef(null)

  // Function to connect to ROS topics
  useEffect(() => {
    if (context.selectedRobot && window.ros && window.rosConnected) {
      setIsConnected(true)
      subscribeToRobotData()
    } else {
      setIsConnected(false)
      unsubscribeAll()
    }

    return () => {
      unsubscribeAll()
    }
  }, [context.selectedRobot, window.rosConnected])

  // Subscribe to robot data
  const subscribeToRobotData = () => {
    // Subscribe to robot position
    subscribeToPosition()

    // Subscribe to laser data
    subscribeToLaserData()

    // Subscribe to map
    subscribeToMap()

    // Subscribe to robot footprint
    subscribeToFootprint()
  }

  // Subscribe to robot position
  const subscribeToPosition = () => {
    if (positionSubscriber.current) {
      unsubscribeFromTopic(positionSubscriber.current)
    }

    positionSubscriber.current = subscribeToRobotPosition(context, (message) => {
      if (!isPaused) {
        setRobotPosition(message)
        setLastUpdate(new Date())

        // Update statistics
        setDataStats(prev => {
          const newTotal = prev.totalUpdates + 1
          const elapsed = (Date.now() - prev.startTime) / 1000
          const currentFps = newTotal / elapsed

          setFps(currentFps.toFixed(1))

          return {
            ...prev,
            totalUpdates: newTotal
          }
        })
      }
    })
  }

  // Subscribe to map
  const subscribeToMap = () => {
    if (mapSubscriber.current) {
      unsubscribeFromTopic(mapSubscriber.current)
    }

    mapSubscriber.current = subscribeToOccupancyMap(context, (message) => {
      if (!isPaused) {
        console.log('Receiving map data:', message)
        setMapData(message)
        // Don't call drawMap here directly, let useEffect do it
        // to maintain synchronization with laser data
      }
    })
  }

  // Subscribe to robot footprint
  const subscribeToFootprint = () => {
    if (footprintSubscriber.current) {
      unsubscribeFromTopic(footprintSubscriber.current)
    }

    if (!context.selectedRobot || !window.ros) return

    console.log('Subscribing to footprint topic: /core/local_costmap/footprint')

    // Try both ROS1 and ROS2 message types
    const messageType = context.selectedRobot?.ROS === 'ROS2' ? 
      'geometry_msgs/msg/PolygonStamped' : 
      'geometry_msgs/PolygonStamped'

    const footprintTopic = new ROSLIB.Topic({
      ros: window.ros,
      name: '/core/local_costmap/footprint',
      messageType: messageType,
      qos: context.selectedRobot?.ROS === 'ROS2' ? {
        durability: 'volatile',
        reliability: 'reliable'
      } : undefined
    })

    footprintSubscriber.current = footprintTopic

    footprintTopic.subscribe((message) => {
      if (!isPaused) {
        console.log('Receiving footprint data:', message)
        
        // Handle different message structures
        let points = null
        if (message.polygon && message.polygon.points) {
          points = message.polygon.points
        } else if (message.points) {
          points = message.points
        } else if (Array.isArray(message)) {
          points = message
        }
        
        if (points && points.length > 2) {
          console.log(`Footprint received: ${points.length} points`)
          setRobotFootprint(points)
        } else {
          console.warn('Invalid footprint data received:', message)
        }
      }
    })

    // Add error handling
    footprintTopic.on('error', (error) => {
      console.error('Footprint topic error:', error)
    })
  }

  // Load background map
  const loadBackgroundMap = () => {
    if (context.selectedRobot?.ip && context.selected?.map?.name) {
      const mapUrl = `http://${context.selectedRobot.ip}/maps/${context.selected.map.name}.png`
      console.log('Loading background map:', mapUrl)

      const img = new Image()
      img.crossOrigin = 'anonymous' // To avoid CORS issues if necessary

      img.onload = () => {
        console.log('Background map loaded successfully')
        setBackgroundMapImage(img)
        setBackgroundMapLoaded(true)
      }

      img.onerror = (error) => {
        console.error('Error loading background map:', error)
        console.log('Attempted URL:', mapUrl)
        setBackgroundMapLoaded(false)
        setBackgroundMapImage(null)
      }

      img.src = mapUrl
    } else {
      console.log('Insufficient information to load map:', {
        robotIp: context.selectedRobot?.ip,
        mapName: context.selected?.map?.name
      })
    }
  }

  // Effect to load background map when robot or map changes
  useEffect(() => {
    loadBackgroundMap()
  }, [context.selectedRobot?.ip, context.selected?.map?.name])

  // Function to measure connection latency
  const measureLatency = async () => {
    if (!context.selectedRobot?.ip) {
      setLatency(null)
      return
    }

    const latencyResult = await measureConnectionLatency(context.selectedRobot.ip)
    setLatency(latencyResult)
    setLastLatencyCheck(new Date())
  }

  // Effect to measure latency periodically
  useEffect(() => {
    if (!context.selectedRobot?.ip) {
      setLatency(null)
      return
    }

    // Measure latency immediately
    measureLatency()

    // Then measure every 5 seconds
    const latencyInterval = setInterval(() => {
      measureLatency()
    }, 5000)

    return () => {
      clearInterval(latencyInterval)
    }
  }, [context.selectedRobot?.ip, isConnected])

  // Subscribe to laser data
  const subscribeToLaserData = () => {
    const laserTopics = [
      // Updated based on available topics from robot
      { name: 'left', topic: '/scan_left_raw' },    // Keep if exists
      { name: 'right', topic: '/scan_right_raw' },  // Keep if exists  
      { name: 'top', topic: '/scan_top_raw' },      // Keep if exists
      { name: 'front', topic: '/scan_front_raw' },  // Updated: was /scan_front, now /scan_front_raw
      { name: 'rear', topic: '/scan_rear_raw' },    // Updated: was /scan_rear, now /scan_rear_raw
      { name: 'generic', topic: '/scan' }           // Generic - DO NOT CHANGE
    ]

    laserTopics.forEach(({ name, topic }) => {
      if (laserSubscribers.current[name]) {
        unsubscribeFromTopic(laserSubscribers.current[name])
      }

      laserSubscribers.current[name] = subscribeToLaserTopic(context, name, topic, (laserName, message) => {
        if (!isPaused) {
          const laserInfo = {
            ranges: message.ranges,
            angle_min: message.angle_min,
            angle_max: message.angle_max,
            angle_increment: message.angle_increment,
            range_min: message.range_min,
            range_max: message.range_max,
            header: message.header
          }

          setLaserData(prev => ({
            ...prev,
            [laserName]: laserInfo
          }))

          // Detect nearby obstacles
          const validRanges = message.ranges.filter(range =>
            range > message.range_min && range < message.range_max
          )

          if (validRanges.length > 0) {
            const minDistance = Math.min(...validRanges)
            const dangerThreshold = 0.5 // 50cm

            setMinDistances(prev => ({
              ...prev,
              [laserName]: minDistance
            }))

            setObstacleAlerts(prev => ({
              ...prev,
              [laserName]: minDistance < dangerThreshold
            }))
          }
        }
      })
    })
  }

  // Disconnect from all subscribers
  const unsubscribeAll = () => {
    unsubscribeFromTopic(positionSubscriber.current)
    positionSubscriber.current = null

    unsubscribeFromTopic(mapSubscriber.current)
    mapSubscriber.current = null

    unsubscribeFromTopics(laserSubscribers.current)
    Object.keys(laserSubscribers.current).forEach(key => {
      laserSubscribers.current[key] = null
    })
    
    // Also unsubscribe from footprint if active
    unsubscribeFromTopic(footprintSubscriber.current)
    footprintSubscriber.current = null
  }

  // TF Capture Functions
  // Test function to simulate footprint data
  const testFootprintWithSimulatedData = () => {
    console.log('Testing with simulated footprint data...')
    
    // Typical robot footprint (rectangular shape)
    const simulatedFootprint = [
      { x: 0.3, y: 0.2 },    // front right
      { x: 0.3, y: -0.2 },   // front left  
      { x: -0.3, y: -0.2 },  // rear left
      { x: -0.3, y: 0.2 }    // rear right
    ]
    
    console.log('Setting simulated footprint:', simulatedFootprint)
    setRobotFootprint(simulatedFootprint)
    
    setTimeout(() => {
      console.log('Removing simulated footprint in 10 seconds...')
      setRobotFootprint(null)
    }, 10000)
  }

  // Function to test with TF data from 192.168.2.214
  const testWithRealTfData = () => {
    console.log('=== TESTING WITH REAL TF DATA ===')
    console.log('Robot: 192.168.2.214:9090')
    console.log('Expected: Individual lasers should match generic representation')
    
    // Test with actual TF transformations that should mirror the generic laser
    const realTfTransforms = {
      // These should match the actual TF data from rostopic echo /tf_static
      left: { x: 0.0, y: 0.3, yaw: Math.PI / 2 },    // 90° rotation
      right: { x: 0.0, y: -0.3, yaw: -Math.PI / 2 }, // -90° rotation
      top: { x: 0.0, y: 0.0, yaw: 0 },               // No rotation
      generic: { x: 0, y: 0, yaw: 0 }                 // Reference
    }
    
    console.log('Testing TF-based transformations:')
    Object.entries(realTfTransforms).forEach(([laser, tf]) => {
      const yawDeg = (tf.yaw * 180 / Math.PI).toFixed(1)
      console.log(`  ${laser}: x=${tf.x}, y=${tf.y}, yaw=${tf.yaw.toFixed(3)} (${yawDeg}°)`)
    })
    
    console.log('\n🔍 Verification:')
    console.log('  - Generic laser shows combined data correctly (reference)')
    console.log('  - Individual lasers should now match this when using TF transforms')
    console.log('  - Left laser: rotated 90° to align with combined view')
    console.log('  - Right laser: rotated -90° to align with combined view')
    console.log('  - Top laser: no rotation needed')
    
    alert('🧪 TF-based alignment test initiated!\n\nCheck console for TF transformation details.\nIndividual lasers should now match the generic representation.')
  }

  // Test function to verify laser alignment for robot at 192.168.2.214
  const testLaserAlignment = () => {
    console.log('=== LASER ALIGNMENT TEST ===')
    console.log('Robot IP for testing: 192.168.2.214:9090')
    console.log('Expected aligned lasers: left, right, top, generic')
    console.log('Current laser transforms:')
    
    Object.entries(laserTransforms).forEach(([laserName, transform]) => {
      const yawDegrees = (transform.yaw * 180 / Math.PI).toFixed(1)
      console.log(`  ${laserName}: x=${transform.x}, y=${transform.y}, yaw=${transform.yaw.toFixed(3)} (${yawDegrees}°)`)
    })
    
    console.log('\nAlignment check:')
    const genericYaw = laserTransforms.generic.yaw
    const misalignedLasers = []
    
    Object.entries(laserTransforms).forEach(([laserName, transform]) => {
      if (laserName !== 'generic') {
        const isAligned = Math.abs(transform.yaw - genericYaw) < 0.01 // tolerance of ~0.6 degrees
        if (!isAligned) {
          misalignedLasers.push(laserName)
        }
        console.log(`  ${laserName}: ${isAligned ? '✅ ALIGNED' : '❌ MISALIGNED'} (yaw diff: ${Math.abs(transform.yaw - genericYaw).toFixed(3)})`)
      }
    })
    
    console.log('\n🗺️ MAP DRAWING CORRECTION:')
    console.log('  - Using TF transformations to match generic laser representation')
    console.log('  - Generic laser = combination of left + right + top with correct TF transforms')
    console.log('  - Individual lasers now use: laserWorldYaw = robotYaw + laserTransform.yaw')
    console.log('  - This mirrors the TF transformation used in the combined generic laser')
    
    if (misalignedLasers.length === 0) {
      console.log('\n🎉 ALL LASERS ARE ALIGNED WITH GENERIC!')
      console.log('✅ Transform values: ALL yaw = 0°')
      console.log('✅ Map drawing: ALL use same orientation')
      alert('✅ All lasers are now aligned!\n\n• Transform values: All yaw = 0°\n• Map drawing: All use same orientation as generic\n• Visual alignment: Fixed on occupancy map')
    } else {
      console.log(`\n⚠️ Found ${misalignedLasers.length} misaligned laser(s): ${misalignedLasers.join(', ')}`)
      alert(`⚠️ Found misaligned lasers: ${misalignedLasers.join(', ')}\nCheck console for details.`)
    }
    
    // Test laser visibility on map
    if (robotPosition && Object.values(laserData).some(data => data !== null)) {
      console.log('\nCurrent laser data status:')
      Object.entries(laserData).forEach(([laserName, data]) => {
        console.log(`  ${laserName}: ${data ? `${data.ranges.length} points` : 'No data'}`)
      })
    }
  }

  // Draw occupancy map
  const drawMap = (mapMessage) => {
    const canvas = mapCanvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    const { width, height } = canvas

    ctx.clearRect(0, 0, width, height)

    // Fill background only if some map is being displayed
    const shouldShowBackgroundMap = backgroundMapLoaded && backgroundMapImage && showBackgroundMap
    const shouldShowRosMap = mapMessage && (showBackgroundMap || !backgroundMapLoaded)

    if (shouldShowBackgroundMap || shouldShowRosMap) {
      ctx.fillStyle = '#f0f0f0'
      ctx.fillRect(0, 0, width, height)
    }

    if (shouldShowBackgroundMap) {
      drawBackgroundMap(ctx, width, height)
    }

    if (shouldShowRosMap) {
      drawOccupancyGrid(ctx, mapMessage, width, height)
    }

    // Draw information on top of everything only if some map is being displayed
    if (shouldShowBackgroundMap || shouldShowRosMap) {
      drawMapInfo(ctx, mapMessage)
    }
  }

  // Draw map information on top of everything
  const drawMapInfo = (ctx, mapMessage) => {
    let startY = 25

    // Background map information
    if (backgroundMapLoaded && backgroundMapImage && showBackgroundMap) {
      ctx.fillStyle = '#333'
      ctx.font = '14px Arial'
      ctx.fillText(`Background Map: ${context.selected?.map?.name || 'N/A'} (Vertical Flip)`, 10, startY)
      startY += 20
      ctx.fillText(`Resolution: ${backgroundMapImage.width}x${backgroundMapImage.height}px`, 10, startY)
      startY += 20
      ctx.fillText(`Robot: ${context.selectedRobot?.ip || 'N/A'}`, 10, startY)
      startY += 20
    }

    // ROS map information
    if (mapMessage) {
      ctx.fillStyle = '#333'
      ctx.font = '12px Arial'
      ctx.fillText(`ROS Map: ${mapMessage.info.width}x${mapMessage.info.height} (${mapMessage.info.resolution.toFixed(3)}m/pixel) (Vertical Flip)`, 10, startY)
      startY += 15
      ctx.fillText(`Origin: (${mapMessage.info.origin.position.x.toFixed(2)}, ${mapMessage.info.origin.position.y.toFixed(2)})`, 10, startY)
      startY += 15
      ctx.fillText(`Map Data Received: ${dataStats.totalUpdates} updates`, 10, startY)
      startY += 15
      ctx.fillText(`Uptime: ${((Date.now() - dataStats.startTime) / 1000).toFixed(0)} seconds`, 10, startY)
    }
  }

  // Draw background map
  const drawBackgroundMap = (ctx, canvasWidth, canvasHeight) => {
    if (!backgroundMapImage) return

    const imageAspect = backgroundMapImage.width / backgroundMapImage.height
    const canvasAspect = canvasWidth / canvasHeight

    let drawWidth, drawHeight, offsetX, offsetY

    if (imageAspect > canvasAspect) {
      drawWidth = canvasWidth * 0.9
      drawHeight = drawWidth / imageAspect
    } else {
      drawHeight = canvasHeight * 0.9
      drawWidth = drawHeight * imageAspect
    }
    offsetX = (canvasWidth - drawWidth) / 2
    offsetY = (canvasHeight - drawHeight) / 2

    // Store scale and offset for coordinate conversion
    if (mapData) { // Prioritize ROS map resolution if available
      const mapResolution = mapData.info.resolution;
      setMapScale(drawWidth / (mapData.info.width * mapResolution));
    } else {
      // Estimate scale if only PNG is available. MAY NEED ADJUSTMENT.
      // Assumes the width of the PNG map corresponds to a certain distance in meters.
      const estimatedMapWidthInMeters = 50; // Example: 50 meters
      setMapScale(drawWidth / estimatedMapWidthInMeters);
    }

    setMapOffset({ x: offsetX, y: offsetY })

    // Apply vertical flip to background map
    ctx.save()
    ctx.translate(offsetX + drawWidth / 2, offsetY + drawHeight / 2)
    ctx.scale(1, -1) // Flip vertical
    ctx.drawImage(backgroundMapImage, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight)
    ctx.restore()
  }

  // Draw OccupancyGrid data
  const drawOccupancyGrid = (ctx, mapMessage, canvasWidth, canvasHeight) => {
    const mapWidth = mapMessage.info.width
    const mapHeight = mapMessage.info.height
    const resolution = mapMessage.info.resolution
    const data = mapMessage.data

    const scaleX = canvasWidth / (mapWidth * resolution)
    const scaleY = canvasHeight / (mapHeight * resolution)
    const scale = Math.min(scaleX, scaleY) * 0.9

    setMapScale(scale)

    const offsetX = (canvasWidth - (mapWidth * resolution * scale)) / 2
    const offsetY = (canvasHeight - (mapHeight * resolution * scale)) / 2
    setMapOffset({ x: offsetX, y: offsetY })

    const imageData = ctx.createImageData(mapWidth, mapHeight)

    for (let i = 0; i < data.length; i++) {
      const val = data[i];
      const r = val === -1 ? 128 : (val === 0 ? 255 : 0);
      const g = val === -1 ? 128 : (val === 0 ? 255 : 0);
      const b = val === -1 ? 128 : (val === 0 ? 255 : 0);

      imageData.data[i * 4] = r;
      imageData.data[i * 4 + 1] = g;
      imageData.data[i * 4 + 2] = b;
      imageData.data[i * 4 + 3] = 255;
    }

    // Create a temporary canvas to draw on and then scale
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = mapWidth;
    tempCanvas.height = mapHeight;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.putImageData(imageData, 0, 0);

    // Apply vertical flip on ROS map too
    ctx.save();
    ctx.translate(offsetX + (mapWidth * resolution * scale) / 2, offsetY + (mapHeight * resolution * scale) / 2);
    ctx.scale(resolution * scale, -resolution * scale); // Vertical flip on ROS map
    ctx.imageSmoothingEnabled = false; // Keeps pixels sharp
    ctx.drawImage(tempCanvas, -mapWidth / 2, -mapHeight / 2);
    ctx.restore();
  }

  // Draw robot and lasers on map
  const drawRobotOnMap = () => {
    const canvas = mapCanvasRef.current;
    if (!canvas || !robotPosition) return;

    const ctx = canvas.getContext('2d');

    // If no ROS map data, use default settings for robot
    let robotCanvasX, robotCanvasY, yaw;

    if (mapData) {
      const resolution = mapData.info.resolution;
      const origin = mapData.info.origin;

      // Convert robot position (meters) to map pixels
      const robotMapX = (robotPosition.position.x - origin.position.x) / resolution;
      const robotMapY = mapData.info.height - ((robotPosition.position.y - origin.position.y) / resolution);

      // Convert to canvas coordinates (with scale and offset)
      robotCanvasX = mapOffset.x + robotMapX * resolution * mapScale;
      robotCanvasY = mapOffset.y + robotMapY * resolution * mapScale;
    } else {
      // If no ROS map, position robot at center of canvas
      robotCanvasX = canvas.width / 2;
      robotCanvasY = canvas.height / 2;
    }

    // Calculate robot orientation
    const quat = robotPosition.orientation;
    yaw = Math.atan2(2 * (quat.w * quat.z + quat.x * quat.y), 1 - 2 * (quat.y * quat.y + quat.z * quat.z));

    // Draw robot footprint or fallback to circle
    if (robotFootprint && robotFootprint.length > 2) {
      console.log(`Drawing footprint with ${robotFootprint.length} points at position (${robotCanvasX}, ${robotCanvasY})`)
      
      // Draw robot footprint polygon
      ctx.fillStyle = 'rgba(74, 144, 226, 0.7)'; // Semi-transparent blue
      ctx.strokeStyle = '#2c5aa0';
      ctx.lineWidth = 3;
      ctx.beginPath();

      // Transform footprint points to canvas coordinates
      let validPoints = 0;
      robotFootprint.forEach((point, index) => {
        // Ensure point has x and y properties
        const px = point.x || 0;
        const py = point.y || 0;
        
        // Transform footprint point (in robot coordinate system) to map coordinates
        const transformedX = px * Math.cos(yaw) - py * Math.sin(yaw);
        const transformedY = px * Math.sin(yaw) + py * Math.cos(yaw);
        
        // Scale to canvas coordinates
        const canvasX = robotCanvasX + (transformedX * mapScale);
        const canvasY = robotCanvasY - (transformedY * mapScale); // Canvas Y is inverted

        if (index === 0) {
          ctx.moveTo(canvasX, canvasY);
          console.log(`Start point: (${px}, ${py}) -> canvas (${canvasX}, ${canvasY})`)
        } else {
          ctx.lineTo(canvasX, canvasY);
        }
        validPoints++;
      });
      
      if (validPoints > 2) {
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        console.log(`Footprint drawn successfully with ${validPoints} points`)
      } else {
        console.warn(`Not enough valid points for footprint: ${validPoints}`)
      }

      // Draw center point for reference
      ctx.fillStyle = '#ff6b6b';
      ctx.beginPath();
      ctx.arc(robotCanvasX, robotCanvasY, 3, 0, 2 * Math.PI);
      ctx.fill();

      // Draw orientation arrow from center
      const arrowLength = 30;
      const arrowX = robotCanvasX + Math.cos(yaw) * arrowLength;
      const arrowY = robotCanvasY - Math.sin(yaw) * arrowLength; // Canvas Y is inverted

      ctx.strokeStyle = '#ff6b6b';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(robotCanvasX, robotCanvasY);
      ctx.lineTo(arrowX, arrowY);
      ctx.stroke();

      // Draw arrowhead
      const headLength = 8;
      const headAngle = Math.PI / 6;
      ctx.beginPath();
      ctx.moveTo(arrowX, arrowY);
      ctx.lineTo(arrowX - headLength * Math.cos(yaw - headAngle), 
                 arrowY + headLength * Math.sin(yaw - headAngle));
      ctx.moveTo(arrowX, arrowY);
      ctx.lineTo(arrowX - headLength * Math.cos(yaw + headAngle), 
                 arrowY + headLength * Math.sin(yaw + headAngle));
      ctx.stroke();
    } else {
      // Fallback to simple circle if no footprint data
      ctx.fillStyle = '#4a90e2';
      ctx.strokeStyle = '#2c5aa0';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(robotCanvasX, robotCanvasY, 8, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      // Draw robot orientation
      const arrowLength = 20;
      const arrowX = robotCanvasX + Math.cos(yaw) * arrowLength;
      const arrowY = robotCanvasY - Math.sin(yaw) * arrowLength; // Canvas Y is inverted

      ctx.strokeStyle = '#ff6b6b';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(robotCanvasX, robotCanvasY);
      ctx.lineTo(arrowX, arrowY);
      ctx.stroke();
    }

    // Draw lasers
    Object.entries(laserData).forEach(([laserName, laserInfo]) => {
      if (laserInfo && laserInfo.ranges && laserVisibility[laserName]) {
        drawLaserOnMap(ctx, laserInfo, robotCanvasX, robotCanvasY, yaw, laserName);
      }
    });
  }

  // Draw laser data on the map
  const drawLaserOnMap = (ctx, laserInfo, robotCanvasX, robotCanvasY, robotYaw, laserName) => {
    const laserTransform = laserTransforms[laserName] || { x: 0, y: 0, yaw: 0 };

    // Laser position relative to robot on canvas
    const laserOffsetX = (laserTransform.x * Math.cos(robotYaw) - laserTransform.y * Math.sin(robotYaw)) * mapScale;
    const laserOffsetY = -(laserTransform.x * Math.sin(robotYaw) + laserTransform.y * Math.cos(robotYaw)) * mapScale; // Canvas Y inverted

    const laserCanvasX = robotCanvasX + laserOffsetX;
    const laserCanvasY = robotCanvasY + laserOffsetY;
    
    // CORRECTED: Apply TF transformation like the generic laser
    // The generic shows correctly because it combines all laser data with proper TF transforms
    // Individual lasers need their TF transformation to match the combined result
    const laserWorldYaw = robotYaw + laserTransform.yaw; // Use TF transformation for correct orientation

    let laserColor = '#ff6b6b';
    if (laserName.includes('front')) laserColor = '#4ecdc4';
    else if (laserName.includes('rear')) laserColor = '#ffe66d';
    else if (laserName.includes('left')) laserColor = '#a8e6cf';
    else if (laserName.includes('right')) laserColor = '#ffaaa5';

    ctx.fillStyle = laserColor;

    laserInfo.ranges.forEach((range, index) => {
      if (range > laserInfo.range_min && range < laserInfo.range_max) {
        const angle = laserInfo.angle_min + index * laserInfo.angle_increment;
        const totalAngle = laserWorldYaw + angle;

        const pointX = laserCanvasX + (range * mapScale * Math.cos(totalAngle));
        const pointY = laserCanvasY - (range * mapScale * Math.sin(totalAngle)); // Canvas Y inverted

        ctx.beginPath();
        ctx.arc(pointX, pointY, 1.5, 0, 2 * Math.PI);
        ctx.fill();
      }
    });
  }

  //Draw laser data on canvas
  const drawLaserData = (laserName, laserInfo) => {
    const canvas = canvasRefs.current[laserName]
    if (!canvas || !laserInfo || !laserInfo.ranges) return

    const ctx = canvas.getContext('2d')
    const width = canvas.width
    const height = canvas.height

    // Canvas clear
    ctx.clearRect(0, 0, width, height)

    // Drawing settings
    const centerX = width / 2
    const centerY = height / 2
    const maxRadius = Math.min(width, height) / 2 - 20
    const scale = maxRadius / laserInfo.range_max
    const dangerThreshold = 0.5

    // Draw reference circles
    ctx.strokeStyle = '#e0e0e0'
    ctx.lineWidth = 1
    for (let i = 1; i <= 4; i++) {
      const radius = (maxRadius / 4) * i
      ctx.beginPath()
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
      ctx.stroke()
    }

    // Draw danger zone
    ctx.strokeStyle = '#ffcccc'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(centerX, centerY, dangerThreshold * scale, 0, 2 * Math.PI)
    ctx.stroke()

    // Draw reference lines
    ctx.strokeStyle = '#e0e0e0'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(centerX, centerY - maxRadius)
    ctx.lineTo(centerX, centerY + maxRadius)
    ctx.moveTo(centerX - maxRadius, centerY)
    ctx.lineTo(centerX + maxRadius, centerY)
    ctx.stroke()

    // Draw robot center
    ctx.fillStyle = '#4a90e2'
    ctx.beginPath()
    ctx.arc(centerX, centerY, 5, 0, 2 * Math.PI)
    ctx.fill()

    // Draw laser data
    const angleStep = (laserInfo.angle_max - laserInfo.angle_min) / laserInfo.ranges.length

    laserInfo.ranges.forEach((range, index) => {
      if (range > laserInfo.range_min && range < laserInfo.range_max) {
        const angle = laserInfo.angle_min + (index * angleStep)
        const x = centerX + (range * scale * Math.cos(angle))
        const y = centerY + (range * scale * Math.sin(angle))

        // Color based on distance
        const isDanger = range < dangerThreshold
        ctx.fillStyle = isDanger ? '#ff4757' : '#2ed573'
        ctx.strokeStyle = isDanger ? '#ff4757' : '#2ed573'

        // Draw line from center to point
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(centerX, centerY)
        ctx.lineTo(x, y)
        ctx.stroke()

        // Draw point
        ctx.beginPath()
        ctx.arc(x, y, isDanger ? 3 : 2, 0, 2 * Math.PI)
        ctx.fill()
      }
    })

    // Draw information
    ctx.fillStyle = '#333'
    ctx.font = '12px Arial'

    // Set color and name based on laser type
    let displayName = laserName.toUpperCase().replace('_', ' ')
    let laserColor = '#4a90e2'

    if (laserName.includes('front')) {
      laserColor = '#4ecdc4'
      displayName = displayName + ' (FRONT)'
    } else if (laserName.includes('rear')) {
      laserColor = '#ffe66d'
      displayName = displayName + ' (REAR)'
    } else if (laserName.includes('left')) {
      laserColor = '#a8e6cf'
      displayName = displayName + ' (LEFT)'
    } else if (laserName.includes('right')) {
      laserColor = '#ffaaa5'
      displayName = displayName + ' (RIGHT)'
    } else if (laserName.includes('top')) {
      laserColor = '#ff8b94'
      displayName = displayName + ' (TOP)'
    } else if (laserName === 'generic') {
      laserColor = '#b4a7d6'
      displayName = 'GENERIC LASER (360°)'
    }

    ctx.fillText(displayName, 10, 20)
    ctx.fillText(`Range: ${laserInfo.range_min.toFixed(1)} - ${laserInfo.range_max.toFixed(1)}m`, 10, 35)
    ctx.fillText(`Points: ${laserInfo.ranges.length}`, 10, 50)

    // Show minimum distance
    const minDist = minDistances[laserName]
    if (minDist !== null) {
      ctx.fillStyle = minDist < dangerThreshold ? '#ff4757' : '#333'
      ctx.fillText(`Min: ${minDist.toFixed(2)}m`, 10, 65)
    }

    // Draw direction indicator
    ctx.fillStyle = laserColor
    ctx.font = 'bold 16px Arial'
    if (laserName.includes('front')) {
      ctx.fillText('↑', centerX - 8, centerY - 80)
    } else if (laserName.includes('rear')) {
      ctx.fillText('↓', centerX - 8, centerY + 95)
    } else if (laserName.includes('left')) {
      ctx.fillText('←', centerX - 85, centerY + 5)
    } else if (laserName.includes('right')) {
      ctx.fillText('→', centerX + 75, centerY + 5)
    } else if (laserName.includes('top')) {
      ctx.fillText('⊕', centerX - 8, centerY - 80)
    } else if (laserName === 'generic') {
      ctx.fillText('⊙', centerX - 8, centerY - 80)
    }
  }

  // Function to control laser visibility on map
  const toggleLaserVisibility = (laserName) => {
    setLaserVisibility(prev => ({
      ...prev,
      [laserName]: !prev[laserName]
    }))
  }

  // Function to enable/disable all lasers
  const toggleAllLasers = (visible) => {
    setLaserVisibility({
      front: visible,
      rear: visible,
      left: visible,
      right: visible,
      top: visible,
      generic: visible
    })
  }

  // Update visualization when laser data changes
  useEffect(() => {
    Object.entries(laserData).forEach(([laserName, laserInfo]) => {
      if (laserInfo) {
        drawLaserData(laserName, laserInfo)
      }
    })

    // Redraw robot on map when data changes or background map loads
    if (mapData || backgroundMapLoaded || robotPosition) {
      // Use the drawMap function to redraw the map
      drawMap(mapData);

      // Draw the robot and lasers on top
      drawRobotOnMap();
    }
  }, [laserData, robotPosition, mapData, backgroundMapLoaded, showBackgroundMap, laserVisibility, robotFootprint])

  // Redraw map when background map loads
  useEffect(() => {
    if (backgroundMapLoaded) {
      // Use the drawMap function to redraw the map
      drawMap(mapData);

      // Redraw robot and lasers if data exists
      if (robotPosition) {
        drawRobotOnMap();
      }
    }
  }, [backgroundMapLoaded])

  // Format position data for display
  const formatPosition = (position) => {
    if (!position) return null

    return {
      x: position.position?.x?.toFixed(3) || 'N/A',
      y: position.position?.y?.toFixed(3) || 'N/A',
      z: position.position?.z?.toFixed(3) || 'N/A',
      orientation: {
        x: position.orientation?.x?.toFixed(3) || 'N/A',
        y: position.orientation?.y?.toFixed(3) || 'N/A',
        z: position.orientation?.z?.toFixed(3) || 'N/A',
        w: position.orientation?.w?.toFixed(3) || 'N/A'
      }
    }
  }

  // Calculate laser data statistics
  const calculateLaserStats = (laserInfo) => {
    if (!laserInfo || !laserInfo.ranges) return null

    const validRanges = laserInfo.ranges.filter(range =>
      range > laserInfo.range_min && range < laserInfo.range_max
    )

    if (validRanges.length === 0) return null

    const min = Math.min(...validRanges)
    const max = Math.max(...validRanges)
    const avg = validRanges.reduce((sum, val) => sum + val, 0) / validRanges.length

    return {
      min: min.toFixed(3),
      max: max.toFixed(3),
      avg: avg.toFixed(3),
      validPoints: validRanges.length,
      totalPoints: laserInfo.ranges.length
    }
  }

  const formattedPosition = formatPosition(robotPosition)

  return (
    <div className="rviz-container">
      <div className="container is-fluid">
        <div className="columns">
          <div className="column">
            <div className="box">
              <h3 className="title is-4">RViz - Real-time Robot Data</h3>

              {/* Connection Status */}
              <div className="field">
                <div className="control">
                  <div className={`tag ${isConnected ? 'is-success' : 'is-danger'}`}>
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </div>
                  {context.selectedRobot && (
                    <div className="tag is-info ml-2">
                      {context.selectedRobot.name} ({context.selectedRobot.ROS || 'ROS1'})
                    </div>
                  )}
                  <div className="tag is-light ml-2">
                    FPS: {fps}
                  </div>
                  <div className={`tag ml-2 ${latency === null ? 'is-warning' :
                      latency < 50 ? 'is-success' :
                        latency < 150 ? 'is-warning' : 'is-danger'
                    }`}>
                    {latency === null ? 'Latency: N/A' : `Latency: ${latency}ms`}
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="field">
                <div className="control">
                  <button
                    className={`button ${isPaused ? 'is-success' : 'is-warning'}`}
                    onClick={() => setIsPaused(!isPaused)}
                  >
                    {isPaused ? '▶️ Resume' : '⏸️ Pause'}
                  </button>
                  <button
                    className="button is-light ml-2"
                    onClick={() => {
                      setDataStats({
                        totalUpdates: 0,
                        startTime: Date.now()
                      })
                      setFps(0)
                      setLatency(null)
                      setLastLatencyCheck(null)
                      measureLatency()
                    }}
                  >
                    🔄 Reset Stats
                  </button>
                  <button
                    className="button is-info ml-2"
                    onClick={() => {
                      console.log('=== FOOTPRINT DEBUG ===')
                      console.log('Robot footprint data:', robotFootprint)
                      console.log('Footprint available:', robotFootprint && robotFootprint.length > 2)
                      console.log('Robot position:', robotPosition)
                      console.log('Footprint subscriber active:', footprintSubscriber.current !== null)
                      if (robotFootprint) {
                        console.log('Footprint points:', robotFootprint.map((p, i) => `${i}: (${p.x}, ${p.y})`))
                      }
                    }}
                    disabled={!context.selectedRobot?.ip}
                  >
                    🔍 Debug Footprint
                  </button>
                  <button
                    className="button is-warning ml-2"
                    onClick={testFootprintWithSimulatedData}
                    disabled={!robotPosition}
                  >
                    🧪 Test Footprint
                  </button>
                  <button
                    className="button is-success ml-2"
                    onClick={testLaserAlignment}
                    title="Test laser alignment for robot at 192.168.2.214"
                  >
                    🎯 Test Laser Alignment
                  </button>
                  <button
                    className="button is-primary ml-2"
                    onClick={testWithRealTfData}
                    title="Test with real TF transformations from robot"
                  >
                    🔧 Test TF Alignment
                  </button>
                </div>
              </div>

              {lastUpdate && (
                <div className="field">
                  <label className="label">Last Update:</label>
                  <div className="control">
                    <span className="tag is-light">
                      {lastUpdate.toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Footprint Status */}
              <div className="field">
                <label className="label">Robot Footprint Status:</label>
                <div className="control">
                  <div className="tags">
                    <span className={`tag ${robotFootprint && robotFootprint.length > 2 ? 'is-success' : 'is-warning'}`}>
                      {robotFootprint && robotFootprint.length > 2 ? 
                        `✅ Active (${robotFootprint.length} points)` : 
                        '⚠️ Not available - using circle'}
                    </span>
                    <span className="tag is-light">
                      Topic: /core/local_costmap/footprint
                    </span>
                    {robotFootprint && robotFootprint.length > 2 && (
                      <span className="tag is-info">
                        📐 Polygon shape
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Laser Alignment Notification */}
              <div className="notification is-success is-light">
                <h6 className="subtitle is-6">🎯 TF-Based Laser Alignment Applied</h6>
                <div className="content">
                  <p><strong>✅ FIXED:</strong> Individual lasers now use TF transformations to match the generic laser representation:</p>
                  <div className="tags">
                    <span className="tag is-success">✅ Left: TF rotation = 90° (π/2)</span>
                    <span className="tag is-success">✅ Right: TF rotation = -90° (-π/2)</span>
                    <span className="tag is-success">✅ Top: TF rotation = 0° (0)</span>
                    <span className="tag is-success">✅ Rear: TF rotation = 180° (π)</span>
                    <span className="tag is-info">📍 Generic: Combined representation</span>
                  </div>
                  <div className="box is-size-7 mt-2" style={{backgroundColor: '#f0f8ff'}}>
                    <strong>🔧 Technical Fix Applied:</strong>
                    <br />• <strong>Problem:</strong> Generic = combination of left+right+top with TF transforms
                    <br />• <strong>Solution:</strong> <code>laserWorldYaw = robotYaw + laserTransform.yaw</code> (using TF data)
                    <br />• <strong>Result:</strong> Individual lasers match combined generic representation
                  </div>
                  <p className="is-size-7 mt-2">
                    <strong>Test robot:</strong> 192.168.2.214:9090 | 
                    <strong> Individual lasers now mirror the generic's TF-based representation.</strong>
                  </p>
                </div>
              </div>

              {/* Latency information */}
              {latency !== null && (
                <div className="field">
                  <label className="label">Connection Status:</label>
                  <div className="control">
                    <div className="tags">
                      <span className={`tag ${latency < 50 ? 'is-success' :
                          latency < 150 ? 'is-warning' : 'is-danger'
                        }`}>
                        {latency < 50 ? '🟢 Excellent' :
                          latency < 150 ? '🟡 Good' : '🔴 Slow'}
                      </span>
                      <span className="tag is-light">
                        Last measurement: {lastLatencyCheck ? lastLatencyCheck.toLocaleTimeString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Obstacle Alerts */}
              {Object.entries(obstacleAlerts).some(([_, hasAlert]) => hasAlert) && (
                <div className="notification is-warning">
                  <h5 className="title is-6">⚠️ Nearby Obstacle Alert</h5>
                  <div className="tags">
                    {Object.entries(obstacleAlerts).map(([laserName, hasAlert]) =>
                      hasAlert && (
                        <span key={laserName} className="tag is-danger">
                          {laserName.toUpperCase()}: {minDistances[laserName]?.toFixed(2)}m
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Laser Status Notification */}
              <div className="notification is-info is-light mt-3">
                <div className="level">
                  <div className="level-left">
                    <div className="level-item">
                      <span className="icon">
                        <i className="fas fa-info-circle"></i>
                      </span>
                      <span>
                        <strong>Active Lasers on Map:</strong> {Object.entries(laserVisibility).filter(([key, value]) => key !== 'generic' && value).length} of {Object.keys(laserVisibility).filter(key => key !== 'generic').length}
                      </span>
                    </div>
                  </div>
                  <div className="level-right">
                    <div className="level-item">
                      <span className="tag is-primary">
                        {Object.values(laserVisibility).every(v => v) ? '✅ All Visible' :
                          Object.values(laserVisibility).every(v => !v) ? '❌ All Hidden' :
                            '⚡ Partially Visible'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Map Visualization with Robot and Lasers */}
        <div className="columns">
          <div className="column">
            <div className="box">
              <div className="level">
                <div className="level-left">
                  <h4 className="title is-5">🗺️ Occupancy Map - Real-time Robot Position</h4>
                </div>
                <div className="level-right">
                  {backgroundMapLoaded && (
                    <div className="field">
                      <div className="control">
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={showBackgroundMap}
                            onChange={(e) => setShowBackgroundMap(e.target.checked)}
                          />
                          <span className="ml-2">🖼️ Show Background Map</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {mapData || backgroundMapLoaded ? (
                <div>
                  <div className="field">
                    <div className="control">
                      <canvas
                        ref={mapCanvasRef}
                        width={800}
                        height={600}
                        style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          backgroundColor: '#fff',
                          display: 'block',
                          margin: '0 auto',
                          cursor: 'grab'
                        }}
                      />
                    </div>
                  </div>

                  <div className="field">
                    <div className="content">
                      <div className="columns">
                        <div className="column">
                          <h6 className="subtitle is-6">Map Information:</h6>
                          <div className="tags">
                            {backgroundMapLoaded && (
                              <>
                                <span className="tag is-success">
                                  🖼️ Background: {context.selected?.map?.name || 'N/A'}
                                </span>
                                <span className="tag is-info">
                                  🤖 Robot: {context.selectedRobot?.ip || 'N/A'}
                                </span>
                              </>
                            )}
                            {mapData && (
                              <>
                                <span className="tag is-info">
                                  📐 ROS: {mapData.info.width}×{mapData.info.height}
                                </span>
                                <span className="tag is-info">
                                  📏 Resolution: {mapData.info.resolution.toFixed(3)}m/pixel
                                </span>
                                <span className="tag is-info">
                                  📍 Origin: ({mapData.info.origin.position.x.toFixed(2)}, {mapData.info.origin.position.y.toFixed(2)})m
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="column">
                          <h6 className="subtitle is-6">Visual Legend:</h6>
                          <div className="tags">
                            <span className="tag" style={{ backgroundColor: '#4a90e2', color: 'white' }}>
                              🤖 Robot
                            </span>
                            <span className="tag" style={{ backgroundColor: '#ff6b6b', color: 'white' }}>
                              ➤ Orientation
                            </span>
                            {!backgroundMapLoaded && (
                              <>
                                <span className="tag" style={{ backgroundColor: '#000', color: 'white' }}>
                                  ⬛ Obstacle
                                </span>
                                <span className="tag" style={{ backgroundColor: '#fff', color: 'black', border: '1px solid #ccc' }}>
                                  ⬜ Free Area
                                </span>
                                <span className="tag" style={{ backgroundColor: '#808080', color: 'white' }}>
                                  ▒ Unknown
                                </span>
                              </>
                            )}
                            {backgroundMapLoaded && mapData && (
                              <span className="tag" style={{ backgroundColor: '#ff0000', color: 'white' }}>
                                🔴 Detected Obstacles
                              </span>
                            )}
                          </div>
                          <div className="tags mt-2">
                            <div className="level">
                              <div className="level-left">
                                <h6 className="subtitle is-6 mb-1">Laser Colors (Click to show/hide on map):</h6>
                              </div>
                              <div className="level-right">
                                <div className="field is-grouped">
                                  <div className="control">
                                    <button
                                      className="button is-small is-success"
                                      onClick={() => toggleAllLasers(true)}
                                      title="Show all lasers"
                                    >
                                      ✅ All
                                    </button>
                                  </div>
                                  <div className="control">
                                    <button
                                      className="button is-small is-danger"
                                      onClick={() => toggleAllLasers(false)}
                                      title="Hide all lasers"
                                    >
                                      ❌ None
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="field is-grouped is-grouped-multiline">
                              <div className="control">
                                <label className="checkbox">
                                  <input
                                    type="checkbox"
                                    checked={laserVisibility.front}
                                    onChange={() => toggleLaserVisibility('front')}
                                  />
                                  <span
                                    className="tag ml-2"
                                    style={{
                                      backgroundColor: laserVisibility.front ? '#4ecdc4' : '#e0e0e0',
                                      color: laserVisibility.front ? 'white' : '#666',
                                      opacity: laserVisibility.front ? 1 : 0.5
                                    }}
                                  >
                                    ● Front Laser
                                  </span>
                                </label>
                              </div>

                              <div className="control">
                                <label className="checkbox">
                                  <input
                                    type="checkbox"
                                    checked={laserVisibility.rear}
                                    onChange={() => toggleLaserVisibility('rear')}
                                  />
                                  <span
                                    className="tag ml-2"
                                    style={{
                                      backgroundColor: laserVisibility.rear ? '#ffe66d' : '#e0e0e0',
                                      color: laserVisibility.rear ? 'black' : '#666',
                                      opacity: laserVisibility.rear ? 1 : 0.5
                                    }}
                                  >
                                    ● Rear Laser (Yellow)
                                  </span>
                                </label>
                              </div>

                              <div className="control">
                                <label className="checkbox">
                                  <input
                                    type="checkbox"
                                    checked={laserVisibility.left}
                                    onChange={() => toggleLaserVisibility('left')}
                                  />
                                  <span
                                    className="tag ml-2"
                                    style={{
                                      backgroundColor: laserVisibility.left ? '#a8e6cf' : '#e0e0e0',
                                      color: laserVisibility.left ? 'black' : '#666',
                                      opacity: laserVisibility.left ? 1 : 0.5
                                    }}
                                  >
                                    ● Left Laser
                                  </span>
                                </label>
                              </div>

                              <div className="control">
                                <label className="checkbox">
                                  <input
                                    type="checkbox"
                                    checked={laserVisibility.right}
                                    onChange={() => toggleLaserVisibility('right')}
                                  />
                                  <span
                                    className="tag ml-2"
                                    style={{
                                      backgroundColor: laserVisibility.right ? '#ffaaa5' : '#e0e0e0',
                                      color: laserVisibility.right ? 'black' : '#666',
                                      opacity: laserVisibility.right ? 1 : 0.5
                                    }}
                                  >
                                    ● Right Laser
                                  </span>
                                </label>
                              </div>

                              <div className="control">
                                <label className="checkbox">
                                  <input
                                    type="checkbox"
                                    checked={laserVisibility.top}
                                    onChange={() => toggleLaserVisibility('top')}
                                  />
                                  <span
                                    className="tag ml-2"
                                    style={{
                                      backgroundColor: laserVisibility.top ? '#ff8b94' : '#e0e0e0',
                                      color: laserVisibility.top ? 'white' : '#666',
                                      opacity: laserVisibility.top ? 1 : 0.5
                                    }}
                                  >
                                    ● Top Laser
                                  </span>
                                </label>
                              </div>

                              <div className="control">
                                <label className="checkbox">
                                  <input
                                    type="checkbox"
                                    checked={laserVisibility.generic}
                                    onChange={() => toggleLaserVisibility('generic')}
                                  />
                                  <span
                                    className="tag ml-2"
                                    style={{
                                      backgroundColor: laserVisibility.generic ? '#b4a7d6' : '#e0e0e0',
                                      color: laserVisibility.generic ? 'white' : '#666',
                                      opacity: laserVisibility.generic ? 1 : 0.5
                                    }}
                                  >
                                    ● Generic Laser (Purple)
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {robotPosition && (
                        <div className="notification is-success is-light">
                          <h6 className="subtitle is-6">📍 Current Robot Position on Map:</h6>
                          <div className="tags">
                            <span className="tag is-primary">
                              X: {robotPosition.position.x.toFixed(3)}m
                            </span>
                            <span className="tag is-primary">
                              Y: {robotPosition.position.y.toFixed(3)}m
                            </span>
                            <span className="tag is-warning">
                              🧭 Orientation: {(() => {
                                const q = robotPosition.orientation
                                const yaw = Math.atan2(2 * (q.w * q.z + q.x * q.y), 1 - 2 * (q.y * q.y + q.z * q.z))
                                return `${(yaw * 180 / Math.PI).toFixed(1)}°`
                              })()}
                            </span>
                            <span className="tag is-info">
                              📡 Active Lasers: {Object.values(laserData).filter(data => data !== null).length}
                            </span>
                            <span className={`tag ${robotFootprint && robotFootprint.length > 2 ? 'is-success' : 'is-warning'}`}>
                              🤖 Footprint: {robotFootprint && robotFootprint.length > 2 ? 
                                `Active (${robotFootprint.length} points)` : 
                                'Circle (no data)'}
                            </span>
                          </div>
                        </div>
                      )}

                      {backgroundMapLoaded && !mapData && (
                        <div className="notification is-warning is-light">
                          <h6 className="subtitle is-6">ℹ️ Background Map Mode</h6>
                          <p>
                            Using background map from robot. For better accuracy, also activate the ROS topic <code>/map</code>
                            to overlay real-time occupancy data.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="notification is-light">
                  <div className="content">
                    <h6 className="subtitle is-6">🔍 Waiting for map data...</h6>
                    <p>Trying to load:</p>
                    <ul className="is-size-7">
                      <li>
                        <strong>Background Map:</strong> {
                          context.selectedRobot?.ip && context.selected?.map?.name
                            ? `http://${context.selectedRobot.ip}/maps/${context.selected.map.name}.png`
                            : 'Robot IP or map name not available'
                        }
                      </li>
                      <li><strong>ROS Topic:</strong> <code>/map</code> (nav_msgs/OccupancyGrid)</li>
                    </ul>
                    <p className="is-size-7 mt-2">
                      <strong>💡 Tips:</strong>
                    </p>
                    <ul className="is-size-7">
                      <li>Check if robot is connected and IP is correct</li>
                      <li>Confirm there is a selected map in the context</li>
                      <li>For ROS data: Run SLAM or load a map with <code>map_server</code></li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="columns">
          {/* Robot Position */}
          <div className="column is-half">
            <div className="box">
              <h4 className="title is-5">Robot Position</h4>
              {formattedPosition ? (
                <div>
                  <div className="field">
                    <label className="label">Position (x, y, z):</label>
                    <div className="control">
                      <div className="tags are-medium">
                        <span className="tag is-primary">X: {formattedPosition.x}</span>
                        <span className="tag is-primary">Y: {formattedPosition.y}</span>
                        <span className="tag is-primary">Z: {formattedPosition.z}</span>
                      </div>
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">Orientation (Quaternion):</label>
                    <div className="control">
                      <div className="tags are-small">
                        <span className="tag is-info">X: {formattedPosition.orientation.x}</span>
                        <span className="tag is-info">Y: {formattedPosition.orientation.y}</span>
                        <span className="tag is-info">Z: {formattedPosition.orientation.z}</span>
                        <span className="tag is-info">W: {formattedPosition.orientation.w}</span>
                      </div>
                    </div>
                  </div>

                  {/* Convert quaternion to rotation angle */}
                  <div className="field">
                    <label className="label">Rotation Angle (Z):</label>
                    <div className="control">
                      <span className="tag is-warning">
                        {(() => {
                          const q = formattedPosition.orientation
                          if (q.x !== 'N/A' && q.y !== 'N/A' && q.z !== 'N/A' && q.w !== 'N/A') {
                            const angle = Math.atan2(2 * (parseFloat(q.w) * parseFloat(q.z) + parseFloat(q.x) * parseFloat(q.y)),
                              1 - 2 * (parseFloat(q.y) * parseFloat(q.y) + parseFloat(q.z) * parseFloat(q.z)))
                            return `${(angle * 180 / Math.PI).toFixed(1)}°`
                        }
                        return 'N/A'
                      })()}
                    </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="notification is-light">
                  <p>Waiting for position data...</p>
                </div>
              )}
            </div>
          </div>

          {/* Laser Data */}
          <div className="column is-half">
            <div className="box">
              <h4 className="title is-5">Laser Data</h4>

              {/* Active Lasers Summary */}
              <div className="notification is-info is-light mb-4">
                <h6 className="subtitle is-6">Active Lasers:</h6>
                <div className="tags">
                  {Object.entries(laserData).map(([laserName, laserInfo]) =>
                    laserInfo && (
                      <span key={laserName} className="tag is-primary">
                        {laserName === 'front' ? 'Front' :
                          laserName === 'rear' ? 'Rear' :
                            laserName === 'left' ? 'Left' :
                              laserName === 'right' ? 'Right' :
                                laserName === 'top' ? 'Top' :
                                  laserName === 'generic' ? '360°' : laserName}
                      </span>
                    )
                  )}
                  {Object.values(laserData).every(data => !data) && (
                    <span className="tag is-warning">No active lasers</span>
                  )}
                </div>
              </div>

              {Object.entries(laserData).map(([laserName, laserInfo]) => {
                const stats = calculateLaserStats(laserInfo)

                return (
                  <div key={laserName} className="mb-4">
                    <h5 className="subtitle is-6 has-text-weight-bold">
                      {laserName === 'front' ? 'Front Laser' :
                        laserName === 'rear' ? 'Rear Laser' :
                          laserName === 'left' ? 'Left Laser' :
                            laserName === 'right' ? 'Right Laser' :
                              laserName === 'top' ? 'Top Laser' :
                                laserName === 'generic' ? 'Generic Laser (360°)' :
                                  `${laserName.charAt(0).toUpperCase() + laserName.slice(1)} Laser`}
                    </h5>

                    {stats ? (
                      <div className="content">
                        <div className="columns is-mobile">
                          <div className="column">
                            <div className="field">
                              <label className="label is-small">Valid Points:</label>
                              <span className="tag is-light">
                                {stats.validPoints} / {stats.totalPoints}
                              </span>
                            </div>
                          </div>
                          <div className="column">
                            {laserInfo.header && (
                              <div className="field">
                                <label className="label is-small">Frame ID:</label>
                                <span className="tag is-light">{laserInfo.header.frame_id}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="notification is-light is-small">
                        <p>Waiting for {
                          laserName === 'front' ? 'front' :
                            laserName === 'rear' ? 'rear' :
                              laserName === 'left' ? 'left' :
                                laserName === 'right' ? 'right' :
                                  laserName === 'top' ? 'top' :
                                    laserName === 'generic' ? 'generic' : laserName
                        } laser data...</p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Technical Information */}
        <div className="columns">
          <div className="column">
            <div className="box">
              <h4 className="title is-5">Technical Information</h4>

              <div className="content">
                <h6 className="subtitle is-6">Monitored ROS Topics:</h6>
                <div className="columns">
                  <div className="column">
                    <h6 className="has-text-weight-semibold">Position and Map:</h6>
                    <ul>
                      <li><code>/wgo/current_position</code> - Current robot position</li>
                      <li><code>/map</code> - Occupancy map (OccupancyGrid)</li>
                      <li><code>/core/local_costmap/footprint</code> - Robot footprint polygon</li>
                    </ul>

                    <h6 className="has-text-weight-semibold">Side Lasers:</h6>
                    <ul>
                      <li><code>/scan_left_raw</code> - Left laser</li>
                      <li><code>/scan_right_raw</code> - Right laser</li>
                      <li><code>/scan_top_raw</code> - Top laser</li>
                    </ul>
                  </div>
                  <div className="column">
                    <h6 className="has-text-weight-semibold">Front/Rear Lasers:</h6>
                    <ul>
                      <li><code>/scan_front</code> - Front laser</li>
                      <li><code>/scan_rear</code> - Rear laser</li>
                    </ul>

                    <h6 className="has-text-weight-semibold">Generic Laser:</h6>
                    <ul>
                      <li><code>/scan</code> - 360° generic laser</li>
                    </ul>
                  </div>
                </div>

                <h6 className="subtitle is-6">Connection Configuration:</h6>
                <div className="tags">
                  <span className="tag is-light">
                    WebSocket: ws://{context.selectedRobot?.ip || 'XXX'}:9090
                  </span>
                  <span className="tag is-light">
                    ROS Version: {context.selectedRobot?.ROS || 'ROS1'}
                  </span>
                </div>

                <h6 className="subtitle is-6">TF-Based Laser Alignment:</h6>
                <div className="content">
                  <p><strong>✅ CORRECTED:</strong> Individual lasers use TF transforms to match generic laser (combined representation):</p>
                  <div className="columns is-mobile">
                    <div className="column">
                      <h6 className="has-text-weight-semibold">TF Transformations Applied:</h6>
                      <ul className="is-size-7">
                        <li>✅ Left laser: yaw = π/2 (90° rotation)</li>
                        <li>✅ Right laser: yaw = -π/2 (-90° rotation)</li>
                        <li>✅ Top laser: yaw = 0 (no rotation)</li>
                        <li>✅ Formula: <code>laserWorldYaw = robotYaw + TF.yaw</code></li>
                      </ul>
                    </div>
                    <div className="column">
                      <h6 className="has-text-weight-semibold">Test Robot (192.168.2.214):</h6>
                      <ul className="is-size-7">
                        <li><code>/scan_left_raw</code> - Individual left</li>
                        <li><code>/scan_right_raw</code> - Individual right</li>
                        <li><code>/scan_top_raw</code> - Individual top</li>
                        <li><code>/scan</code> - Combined (left+right+top)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Rviz
